/// <reference types="vite/client" />
// ADR-0007 U7: a fetch-based SSE reader for K2. Native EventSource cannot set the Authorization header
// (which would force the bearer into the URL and leak it into logs — C4), so we read the stream with
// fetch + ReadableStream and attach `Authorization: Bearer <token>` from the existing Clerk token path.
// Reconnect is manual (we want it anyway, to drive refetch-on-reconnect, KTD7).

import { acquireToken } from './auth/auth.js';

const configuredBaseUrl = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
// Node test runs (vitest, no DOM) have no `window`; the origin is only meaningful in a browser.
const BASE_URL = configuredBaseUrl || (typeof window !== 'undefined' ? window.location.origin : '');

/** A parsed SSE frame: the event name, the optional id, and the raw `data:` payload. */
export interface SseFrame {
  event: string;
  id?: string;
  data: string;
}

/** The four explicit connection states (ADR-0007 KTD12). */
export type SseConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'stale';

export interface SseClientOptions {
  /** The org to scope the stream to (the query param; never in the JWT). */
  orgId: string;
  /** The venue scope: one exchange id or the comma form ('2,6', ADR-0012 D9); omit for an unscoped stream. */
  exchange?: number | string;
  /**
   * The optional run-mode scope (ADR-0040 M12): 'Paper' | 'Live' narrows the stream to that mode's events
   * (mode-agnostic events still flow; an unknown name is a 400). Omit for an unscoped stream.
   */
  mode?: string | null;
  /** Called for each parsed event frame. */
  onEvent: (frame: SseFrame) => void;
  /** Called on every connection-state transition. */
  onStateChange: (state: SseConnectionState) => void;
  /** Called on each successful (re)connect — the host refetches authoritative state here (KTD7). */
  onConnected: () => void;
}

/**
 * Opens and maintains a single authenticated SSE connection. Reconnects with a freshly acquired token on
 * drop (KTD3), with capped exponential backoff. Call `close()` to tear down (e.g. on org switch or logout).
 */
/**
 * The stream URL for a scope: `orgId` always; `exchange` (the venue set) and `mode` (the run-mode filter)
 * only when given. Pure, so the scope → URL contract is unit-testable without opening a connection.
 */
export function buildStreamUrl(options: Pick<SseClientOptions, 'orgId' | 'exchange' | 'mode'>): string {
  const params = new URLSearchParams({ orgId: options.orgId });
  if (options.exchange != null && options.exchange !== '') params.append('exchange', String(options.exchange));
  if (options.mode) params.append('mode', options.mode);
  return `${BASE_URL}/api/stream?${params}`;
}

export class SseClient {
  private controller: AbortController | null = null;
  private closed = false;
  private backoffMs = 1000;
  private readonly maxBackoffMs = 30000;

  constructor(private readonly options: SseClientOptions) {}

  start(): void {
    this.closed = false;
    void this.connectLoop();
  }

  close(): void {
    this.closed = true;
    this.controller?.abort();
    this.controller = null;
  }

  private setState(state: SseConnectionState): void {
    if (!this.closed) {
      this.options.onStateChange(state);
    }
  }

  /** A connection must stay up at least this long to count as "stable" and reset the backoff. */
  private readonly stableThresholdMs = 10000;

  /**
   * Whether the next reconnect is a PLANNED rollover: the server closed a STABLE stream cleanly. The server
   * caps every connection at min(token exp, MaxConnectionTtl), so with a short-lived Clerk JWT a healthy
   * session is force-closed and re-opened on a fixed cadence — flashing "Reconnecting" at the operator every
   * token lifetime for what is routine rollover. A planned rollover keeps showing "Live" and reconnects
   * immediately; only a FAILED rollover (or an unstable drop) degrades the indicator.
   */
  private seamlessRollover = false;

  private async connectLoop(): Promise<void> {
    while (!this.closed) {
      this.controller = new AbortController();
      let connectedAt = 0;
      const seamless = this.seamlessRollover;
      this.seamlessRollover = false;
      try {
        if (!seamless) {
          this.setState(this.backoffMs === 1000 ? 'connecting' : 'reconnecting');
        }
        const token = await acquireToken();
        if (!token) {
          throw new Error('No auth token available for SSE connection.');
        }

        const url = buildStreamUrl(this.options);
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
          signal: this.controller.signal,
        });

        if (!response.ok || !response.body) {
          // 401/403 etc. — back off and retry with a fresh token (the session may have refreshed).
          throw new Error(`SSE connect failed: ${response.status}`);
        }

        // Connected. Signal the host to refetch authoritative state (KTD7). NOTE: do NOT reset the backoff here
        // — a server that accepts then immediately closes (TTL hit, transient proxy drop) would otherwise make
        // us reconnect + re-acquire-token + refetch every ~1s forever (a reconnect/refetch STORM that can starve
        // the browser's per-host connection pool and the REST API). The backoff is reset only AFTER the stream
        // has stayed up past `stableThresholdMs` (below), so a flapping connection backs off exponentially.
        connectedAt = Date.now();
        this.setState('connected');
        this.options.onConnected();

        await this.readStream(response.body);

        // The server ended the stream CLEANLY (no error). If the connection was stable, this is the routine
        // token-TTL rollover — reconnect straight away without degrading the indicator. An unstable clean
        // close (accept-then-close flap) still takes the visible backoff path.
        if (!this.closed && Date.now() - connectedAt >= this.stableThresholdMs) {
          this.seamlessRollover = true;
        }
      } catch (err) {
        if (this.closed) {
          return;
        }
        // Stream dropped/failed — show degraded, back off, then reconnect with a fresh token.
        this.setState('reconnecting');
      }

      if (this.closed) {
        return;
      }

      // Reset the backoff only if the connection proved stable (stayed up long enough); otherwise keep
      // escalating so a flapping endpoint can't drive a 1/sec reconnect storm.
      if (connectedAt > 0 && Date.now() - connectedAt >= this.stableThresholdMs) {
        this.backoffMs = 1000;
      }

      if (!this.seamlessRollover) {
        await this.delay(this.backoffMs);
        this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs);
      }
    }
  }

  private async readStream(body: ReadableStream<Uint8Array>): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (!this.closed) {
      const { done, value } = await reader.read();
      if (done) {
        // Server closed the stream (TTL/shutdown). Treat as a drop -> reconnect.
        return;
      }
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line.
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const rawFrame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const frame = parseFrame(rawFrame);
        if (frame) {
          this.options.onEvent(frame);
        }
      }
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/** Parse a raw SSE frame block into {event, id, data}. Comment lines (`:`) and keepalives are ignored. */
function parseFrame(raw: string): SseFrame | null {
  let event = 'message';
  let id: string | undefined;
  const dataLines: string[] = [];

  for (const line of raw.split('\n')) {
    if (line.startsWith(':') || line.length === 0) {
      continue; // comment / keepalive
    }
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
    } else if (line.startsWith('id:')) {
      id = line.slice(3).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /, ''));
    }
  }

  if (dataLines.length === 0) {
    return null; // a pure comment/keepalive frame
  }
  return { event, id, data: dataLines.join('\n') };
}
